import { Injectable } from '@angular/core';
import { HangerService } from '@app/services/api/hanger.service';
import { FlashService } from '@app/services/flash.service';
import {
  AddAll,
  AddOne,
  LoadFail,
  LoadSuccess,
  HangerActionTypes,
  Create,
  CreateFail,
  CreateSuccess,
  UpdateHanger,
  UpdateHangerSuccess,
  UpdateFail,
  UpdateHangerOne,
  DeleteHanger,
  DeleteHangerSuccess,
  DeleteHangerFail
} from '@core/actions/hanger.actions';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Hanger } from '@app/models/hanger';


@Injectable()
export class HangerEffects {
  constructor(
    private actions$: Actions,
    private hangersApi: HangerService,
    private fs: FlashService
  ) { }

  @Effect()
  load$: Observable<Action> = this.actions$.pipe(
    ofType(HangerActionTypes.Load),
    switchMap(action =>
      this.hangersApi.get(action).pipe(
        map(hangers => new LoadSuccess(hangers)),
        catchError(error => of(new LoadFail(error)))
      )
    )
  );

  @Effect()
  loadSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<LoadSuccess>(HangerActionTypes.LoadSuccess),
    map(action => new AddAll(action.hangers))
  );

  @Effect()
  create$: Observable<Action> = this.actions$.pipe(
    ofType<Create>(HangerActionTypes.Create),
    map(action => action.hanger),
    switchMap(hanger => this.hangersApi.create(hanger).pipe(
      map((savedHanger: Hanger) => new CreateSuccess(savedHanger)),
      catchError(error => of(new CreateFail(error))))));

  @Effect()
  createSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<CreateSuccess>(HangerActionTypes.CreateSuccess),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Colgador creado' });
      return new AddOne(action.hanger);
    })
  );

  @Effect()
  update$: Observable<Action> = this.actions$.pipe(
    ofType<UpdateHanger>(HangerActionTypes.UpdateHanger),
    map(action => [action.id, action.hanger]),
    switchMap(([id, data]) => {
      return this.hangersApi.update(id, data).pipe(
        map((hangers: Hanger) => new UpdateHangerSuccess(id, hangers)),
        catchError(error => of(new UpdateFail(error)))
      );
    })
  );

  @Effect()
  updateSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<UpdateHangerSuccess>(HangerActionTypes.UpdateHangerSuccess),
    map(action => {
      this.fs.emit({ type: 'success', message: 'Colgador actualizado' });
      return new UpdateHangerOne(action.data);
    })
  );

  @Effect({ dispatch: false })
  updateFail$ = this.actions$.pipe(
    ofType<UpdateFail>(HangerActionTypes.UpdateFail),
    map(action => action.error),
    tap(res =>
      this.fs.emit({
        message: 'No se pudo crear el colgador',
        type: 'danger'
      })
    )
  );

  @Effect()
  deleteHanger$: Observable<Action> = this.actions$.pipe(
    ofType<DeleteHanger>(HangerActionTypes.DeleteHanger),
    map(action => [action.hangerId]),
    switchMap(([hangerId]) => {
      return this.hangersApi.delete(hangerId).pipe(
        map((hangers: any) => new DeleteHangerSuccess()),
        catchError(error => of(new DeleteHangerFail(error)))
      );
    })
  );

  @Effect({ dispatch: false })
  deleteHangerSuccess$: Observable<Action> = this.actions$.pipe(
    ofType<DeleteHangerSuccess>(HangerActionTypes.DeleteHangerSuccess),
    map(action => action.data),
    tap(res =>
      this.fs.emit({
        message: 'Colgador eliminado',
        type: 'success'
      })
    )
  );

  @Effect({ dispatch: false })
  deleteHangerFail$ = this.actions$.pipe(
    ofType<DeleteHangerFail>(HangerActionTypes.DeleteHangerFail),
    map(action => action.error),
    tap(res =>
      this.fs.emit({
        message: 'No se pudo crear el colgador',
        type: 'danger'
      })
    )
  );
}
